import axios, { CancelTokenSource } from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Notification from 'components/notification';
import { isRetryableError } from 'constants/errors/codes';
import { REQUEST_TIMEOUT_MS } from 'constants/requests';
import {
  buildIdList,
  getPageSlice,
  isSameIdList,
  playlistInitialValues,
  playlistUsersSummaryInitialValues,
  toPositiveNumber,
} from 'contexts/playlists/utils';
import {
  IPlaylist,
  IPlaylistContentHook,
  IPlaylistUserListItemResponse,
  IPlaylistUsersSummary,
  PlaylistDeadlineFilterValue,
} from '../contexts/playlists/types';
import useSortState from './useSortState';
import {
  fromOrdering,
  getNextSortState,
  loadSortState,
  saveSortState,
  toOrdering,
} from 'utils/sorting';
import { SortState } from 'types/sorting';
import { PLAYLIST_TYPE } from 'constants/playlists';
import {
  deletePlaylist,
  editPlaylist,
  getPlaylist,
  getPlaylistAssignedTeams,
  getPlaylistUsersList,
  unAssignUsers,
} from 'services/playlists';
import { SessionContext } from 'contexts/session';

const ALL_DEADLINE_FILTER_VALUES: PlaylistDeadlineFilterValue[] = [
  'overdue',
  'upcoming',
  'completed_on_time',
  'none',
];
const DEADLINE_FILTER_DEBOUNCE_MS = 500;

const DEADLINE_STATUS_QUERY_MAP: Record<
  PlaylistDeadlineFilterValue,
  Array<'overdue' | 'upcoming' | 'completed_on_time' | 'completed_overdue' | 'none'>
> = {
  overdue: ['overdue'],
  upcoming: ['upcoming'],
  completed_on_time: ['completed_on_time', 'completed_overdue'],
  none: ['none'],
};

const areAllDeadlineFiltersSelected = (selectedValues: PlaylistDeadlineFilterValue[]): boolean =>
  selectedValues.length === ALL_DEADLINE_FILTER_VALUES.length &&
  ALL_DEADLINE_FILTER_VALUES.every((value) => selectedValues.includes(value));

const getDeadlineStatusParam = (
  selectedValues: PlaylistDeadlineFilterValue[]
): string | undefined => {
  if (selectedValues.length === 0 || areAllDeadlineFiltersSelected(selectedValues)) {
    return undefined;
  }

  return Array.from(
    new Set(selectedValues.flatMap((selectedValue) => DEADLINE_STATUS_QUERY_MAP[selectedValue]))
  ).join(',');
};

const PLAYLIST_USERS_COL_MAP: Record<string, string> = {
  'pl-user': 'user',
  'pl-last-activity': 'last_activity_date',
  'pl-items-started': 'items_started',
  'pl-items-completed': 'trackable_items_finished',
  'pl-deadline': 'due_date',
};

const usePlaylistData = (): IPlaylistContentHook => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const [playlist, setPlaylist] = useState(playlistInitialValues);
  const [fetching, setFetching] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const session = useContext(SessionContext);
  const { account } = session || {};
  const isAccountOwner = Boolean(session?.isAccountOwner);
  const isTeamManager = Boolean(session?.isTeamManager);

  const [playlistUsers, setPlaylistUsers] = useState<IPlaylistUserListItemResponse[]>([]);
  const [playlistUsersSummary, setPlaylistUsersSummary] = useState<IPlaylistUsersSummary>({
    ...playlistUsersSummaryInitialValues,
    is_account_playlist: Boolean(playlistInitialValues.is_account_playlist),
  });
  const [usersPagination, setUsersPagination] = useState({ page: 1, page_size: 9 });
  const [selectedDeadlineFilters, setSelectedDeadlineFilters] = useState<
    PlaylistDeadlineFilterValue[]
  >([]);
  const { sortState, setSortState } = useSortState(
    loadSortState({ table: 'playlist-users', variant: 'assigned', context: id })
  );
  const firstPageUsersIdsRef = useRef<Record<string, string[]>>({});
  const deadlineFilterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playlistUsersRequestSourceRef = useRef<CancelTokenSource | null>(null);
  const playlistUsersRequestIdRef = useRef(0);
  const lastRefreshRetryableRef = useRef(true);

  const business_account_id = account?.id;

  const getIsSonarPlaylist = () =>
    [PLAYLIST_TYPE.SONAR_RECOMMENDED, PLAYLIST_TYPE.SONAR_GOAL].includes(playlist.type);

  const clearDeadlineFilterDebounce = () => {
    if (deadlineFilterTimeoutRef.current) {
      clearTimeout(deadlineFilterTimeoutRef.current);
      deadlineFilterTimeoutRef.current = null;
    }
  };

  const cancelPlaylistUsersRequest = () => {
    if (playlistUsersRequestSourceRef.current) {
      playlistUsersRequestSourceRef.current.cancel(
        'Canceled previous playlist users request in favor of a newer one.'
      );
      playlistUsersRequestSourceRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (deadlineFilterTimeoutRef.current) {
        clearTimeout(deadlineFilterTimeoutRef.current);
        deadlineFilterTimeoutRef.current = null;
      }
      cancelPlaylistUsersRequest();
    },
    []
  );

  const getPlaylistData = async (options?: { silent?: boolean }): Promise<IPlaylist | null> => {
    const silent = Boolean(options?.silent);
    try {
      if (!silent) {
        setFetching(true);
      }
      const response = await getPlaylist(
        {
          id,
          business_account_id,
        },
        silent ? { timeoutMs: REQUEST_TIMEOUT_MS } : undefined
      );
      if (response.status === 200) {
        const { data } = response;
        response.status === 200 && setPlaylist(data);
        if (!silent) {
          setFetching(false);
        }
        return data;
      }
      // An unexpected non-200 without a thrown error: treat as worth retrying rather than
      // stopping a caller (e.g. the duration refresh loop) on a fluke.
      lastRefreshRetryableRef.current = true;
    } catch (error) {
      lastRefreshRetryableRef.current = isRetryableError(error);
      const msg = error?.detail
        ? error?.detail
        : 'Something went wrong, if it repeats please contact support.';
      Notification({ text: msg, type: 'error' });
    }
    if (!silent) {
      setFetching(false);
    }
    return null;
  };

  const callEditPlaylist = async (data) => {
    setFetching(true);
    try {
      const response = await editPlaylist(data);

      if (response.status === 200) {
        Notification({ text: `${response.data.name} playlist has been updated`, type: 'success' });
        setFetching(false);
        return true;
      } else {
        Notification({ text: 'There has been a problem editing your playlist', type: 'error' });
      }
    } catch (error) {
      if (error?.response?.data?.details) {
        Notification({ text: error.response.data.details, type: 'error' });
      }
    }
    setFetching(false);
    return false;
  };

  const callDeletePlaylist = async (playlist: IPlaylist) => {
    try {
      setFetching(true);
      await deletePlaylist({
        id: playlist.id,
        business_account_id,
      });
      setFetching(false);
      Notification({ text: `${playlist.name} has been deleted`, type: 'success' });
      history.push('/playlists');
    } catch (error) {
      Notification({ text: 'There has been a problem deleting your playlist', type: 'error' });
    }
  };

  const fetchPlaylistUsers = async (
    _isSonar: boolean,
    pagination?: { page?: number; page_size?: number },
    queryOverrides?: {
      selectedDeadlineFilters?: PlaylistDeadlineFilterValue[];
      sortState?: SortState;
    }
  ) => {
    if (!playlist?.id || !business_account_id) {
      cancelPlaylistUsersRequest();
      setPlaylistUsers([]);
      setPlaylistUsersSummary({
        ...playlistUsersSummaryInitialValues,
        is_account_playlist: Boolean(playlist?.is_account_playlist),
        teams_assigned: [],
      });
      return;
    }

    const effectiveDeadlineFilters =
      queryOverrides?.selectedDeadlineFilters ?? selectedDeadlineFilters;
    const effectiveSortState = queryOverrides?.sortState ?? sortState;
    const requestId = playlistUsersRequestIdRef.current + 1;
    playlistUsersRequestIdRef.current = requestId;
    cancelPlaylistUsersRequest();
    const requestSource = axios.CancelToken.source();
    playlistUsersRequestSourceRef.current = requestSource;

    setFetchingUsers(true);
    try {
      const canFetchAssignedTeams = !_isSonar && (isAccountOwner || isTeamManager);
      const assignedTeamsPromise = canFetchAssignedTeams
        ? getPlaylistAssignedTeams({
            account_id: business_account_id,
            playlist_id: playlist.id,
          }).catch(() => null)
        : Promise.resolve(null);
      const page = toPositiveNumber(pagination?.page ?? usersPagination.page, 1);
      const pageSize = toPositiveNumber(pagination?.page_size ?? usersPagination.page_size, 9);
      const deadlineStatusParam = getDeadlineStatusParam(effectiveDeadlineFilters);
      const ordering = toOrdering(effectiveSortState, PLAYLIST_USERS_COL_MAP) ?? undefined;
      const responseUsers = await getPlaylistUsersList({
        account_id: business_account_id,
        playlist_id: playlist.id,
        page,
        page_size: pageSize,
        ...(deadlineStatusParam ? { deadline_status: deadlineStatusParam } : {}),
        ...(ordering ? { ordering } : {}),
        cancelToken: requestSource.token,
      });
      const assignedTeamsResponse = await assignedTeamsPromise;
      if (requestId !== playlistUsersRequestIdRef.current) {
        return;
      }
      if (responseUsers.status === 200) {
        const { summary, results, count } = responseUsers.data;
        const usersResults = Array.isArray(results) ? results : [];
        const teamsAssignedFromEndpoint = assignedTeamsResponse?.data?.teams_assigned;
        const teamsAssignedFromSummary = summary?.teams_assigned;
        const teams_assigned = Array.isArray(teamsAssignedFromEndpoint)
          ? teamsAssignedFromEndpoint
          : Array.isArray(teamsAssignedFromSummary)
          ? teamsAssignedFromSummary
          : [];
        const totalUsers =
          typeof summary?.total_users === 'number'
            ? summary.total_users
            : typeof count === 'number'
            ? count
            : usersResults.length;
        const currentIds = buildIdList(usersResults, (item) => item?.user?.id ?? '');
        const filterKey = JSON.stringify({
          page_size: pageSize,
          playlist_id: playlist.id,
          account_id: business_account_id,
          deadline_status: deadlineStatusParam ?? '',
          ordering: ordering ?? '',
        });
        if (page === 1) {
          firstPageUsersIdsRef.current[filterKey] = currentIds;
        }
        const firstPageIds = firstPageUsersIdsRef.current[filterKey] ?? [];
        // If page > 1 returns the exact same IDs as page 1,
        // the backend is ignoring `page` and we need a client-side slice fallback.
        const isSameAsFirstPage = isSameIdList(firstPageIds, currentIds);

        const shouldPaginateClientSide = usersResults.length > pageSize;
        const { slice, startIndex, endIndex } = getPageSlice(usersResults, page, pageSize);
        let safeResults = shouldPaginateClientSide ? slice : usersResults;

        if (
          !shouldPaginateClientSide &&
          page > 1 &&
          usersResults.length === pageSize &&
          totalUsers > pageSize &&
          isSameAsFirstPage
        ) {
          const fallbackPageSize = Math.min(page * pageSize, 200);
          if (fallbackPageSize > pageSize) {
            const fallbackResponse = await getPlaylistUsersList({
              account_id: business_account_id,
              playlist_id: playlist.id,
              page: 1,
              page_size: fallbackPageSize,
              ...(deadlineStatusParam ? { deadline_status: deadlineStatusParam } : {}),
              ...(ordering ? { ordering } : {}),
              cancelToken: requestSource.token,
            });
            if (requestId !== playlistUsersRequestIdRef.current) {
              return;
            }
            if (fallbackResponse.status === 200) {
              const fallbackResults = Array.isArray(fallbackResponse.data?.results)
                ? fallbackResponse.data.results
                : [];
              if (fallbackResults.length > startIndex) {
                safeResults = fallbackResults.slice(startIndex, endIndex);
              }
            }
          }
        }

        setPlaylistUsers(safeResults);
        setPlaylistUsersSummary({
          total_users: totalUsers,
          is_account_playlist:
            typeof summary?.is_account_playlist === 'boolean'
              ? summary.is_account_playlist
              : Boolean(playlist?.is_account_playlist),
          teams_assigned,
        });
      }
    } catch (error) {
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
        return;
      }
      if (requestId !== playlistUsersRequestIdRef.current) {
        return;
      }
      Notification({ text: 'There has been a problem', type: 'error' });
      setPlaylistUsersSummary({
        ...playlistUsersSummaryInitialValues,
        is_account_playlist: Boolean(playlist?.is_account_playlist),
        teams_assigned: [],
      });
    }
    if (requestId === playlistUsersRequestIdRef.current) {
      playlistUsersRequestSourceRef.current = null;
      setFetchingUsers(false);
    }
  };

  const callGetUsers = async (
    _isSonar: boolean,
    pagination?: { page?: number; page_size?: number }
  ) => {
    clearDeadlineFilterDebounce();
    return fetchPlaylistUsers(_isSonar, pagination);
  };

  const callGetAssignedTeams = async (isSonar: boolean) => {
    const canFetchAssignedTeams = !isSonar && (isAccountOwner || isTeamManager);
    if (!playlist?.id || !business_account_id || !canFetchAssignedTeams) {
      setPlaylistUsersSummary((prev) => ({ ...prev, teams_assigned: [] }));
      return;
    }

    try {
      const response = await getPlaylistAssignedTeams({
        account_id: business_account_id,
        playlist_id: playlist.id,
      });
      const teams_assigned = Array.isArray(response?.data?.teams_assigned)
        ? response.data.teams_assigned
        : [];
      setPlaylistUsersSummary((prev) => ({ ...prev, teams_assigned }));
    } catch (error) {
      Notification({ text: 'There has been a problem', type: 'error' });
      setPlaylistUsersSummary((prev) => ({ ...prev, teams_assigned: [] }));
    }
  };

  const onUsersPaginationChange = (value: string, name: string) => {
    clearDeadlineFilterDebounce();
    const nextPageSize =
      name === 'page_size'
        ? toPositiveNumber(value, usersPagination.page_size)
        : usersPagination.page_size;
    const nextPage = name === 'page' ? toPositiveNumber(value, 1) : 1;
    const nextPagination = { page: nextPage, page_size: nextPageSize };
    setUsersPagination(nextPagination);
    fetchPlaylistUsers(getIsSonarPlaylist(), nextPagination);
  };

  const onDeadlineFiltersChange = (nextSelectedValues: PlaylistDeadlineFilterValue[]) => {
    setSelectedDeadlineFilters(nextSelectedValues);
    const nextPagination = { ...usersPagination, page: 1 };
    setUsersPagination(nextPagination);
    // Debounce delays fetchPlaylistUsers, so flip loading now to avoid empty-state flicker.
    setFetchingUsers(true);
    clearDeadlineFilterDebounce();
    deadlineFilterTimeoutRef.current = setTimeout(() => {
      fetchPlaylistUsers(getIsSonarPlaylist(), nextPagination, {
        selectedDeadlineFilters: nextSelectedValues,
      });
      deadlineFilterTimeoutRef.current = null;
    }, DEADLINE_FILTER_DEBOUNCE_MS);
  };

  const onSortToggle = (columnKey: string) => {
    clearDeadlineFilterDebounce();
    const nextSortState = getNextSortState(sortState, columnKey);
    setSortState(nextSortState);
    saveSortState({ table: 'playlist-users', variant: 'assigned', context: id }, nextSortState);
    const nextPagination = { ...usersPagination, page: 1 };
    setUsersPagination(nextPagination);
    fetchPlaylistUsers(getIsSonarPlaylist(), nextPagination, {
      sortState: nextSortState,
    });
  };

  // Mobile sort dropdown emits an explicit ordering value (e.g. 'pl-user' /
  // '-pl-user', or '' to clear), unlike onSortToggle which cycles. Set the
  // state directly from that value.
  const onSortSelect = (value: string) => {
    clearDeadlineFilterDebounce();
    const nextSortState = value ? fromOrdering(value) : { columnKey: null, direction: null };
    setSortState(nextSortState);
    saveSortState({ table: 'playlist-users', variant: 'assigned', context: id }, nextSortState);
    const nextPagination = { ...usersPagination, page: 1 };
    setUsersPagination(nextPagination);
    fetchPlaylistUsers(getIsSonarPlaylist(), nextPagination, {
      sortState: nextSortState,
    });
  };

  const callRemoveUser = async (item: IPlaylistUserListItemResponse): Promise<boolean> => {
    setFetchingUsers(true);
    try {
      await unAssignUsers({
        business_account_id,
        user_ids: [item.user.id],
        id: playlist.id,
      });
      const isSonar = getIsSonarPlaylist();
      const shouldGoBackPage = usersPagination.page > 1 && playlistUsers.length === 1;
      const nextPage = shouldGoBackPage ? usersPagination.page - 1 : usersPagination.page;
      const nextPagination = {
        ...usersPagination,
        page: nextPage,
      };
      if (shouldGoBackPage) {
        setUsersPagination(nextPagination);
      }
      await fetchPlaylistUsers(isSonar, nextPagination);
      return true;
    } catch (error) {
      Notification({ text: 'There has been a problem', type: 'error' });
      return false;
    }
  };

  return {
    fetching,
    setFetching,
    setPlaylist,
    fetchingUsers,
    playlist,
    playlistUsers,
    playlistUsersSummary,
    usersPagination,
    selectedDeadlineFilters,
    sortState,
    error: false,
    refreshPlaylist: getPlaylistData,
    isLastRefreshRetryable: () => lastRefreshRetryableRef.current,
    callEditPlaylist,
    callDeletePlaylist,
    callGetUsers,
    onDeadlineFiltersChange,
    onSortToggle,
    onSortSelect,
    callGetAssignedTeams,
    callRemoveUser,
    onUsersPaginationChange,
  };
};

export default usePlaylistData;
